import React, {useMemo} from "react";
import HighchartsReact from "highcharts-react-official";
import { blue, red, green, orange, purple, teal, pink, indigo } from '@mui/material/colors'

import Stack from "@mui/material/Stack";
import {useTranslation} from "react-i18next";
import Highcharts, {chartLogoOptions, creditsOptions, exportingOptions} from "../highChartsBase";
import useHighChartLocaleOptions from "../useHighChartLocaleOptions";

const colors = [blue[900], red[500], green[500], orange[500], purple[500], teal[500], pink[500], indigo[500]];

const TimeseriesHSComparisonGraph = ({stationsData, stations, highlightedDate}) => {
    const {t} = useTranslation(["graphs", "webgis"]);
    const highChartLocaleOptions = useHighChartLocaleOptions();

    const series = useMemo(() => {
        return (stations || []).map((station, index) => {
            const stationId = station.id;
            const data = (stationsData[stationId] || []).map(item => [
                new Date(item.timestamp).getTime(),
                item.hs
            ]);

            return {
                id: `hs-station-${stationId}`,
                data: data,
                type: 'line',
                name: `${station.code || station.name}`,
                color: colors[index % colors.length],
                yAxis: 0,
                marker: {
                    enabled: false,
                    radius: 3
                }
            };
        });
    }, [stations, stationsData]);

    const options = useMemo(() => {
        const xAxis = {
            type: 'datetime',
            gridLineWidth: 1
        };

        if (highlightedDate) {
            xAxis.plotLines = [{
                color: 'red',
                value: new Date(highlightedDate).getTime(),
                width: 1,
                dashStyle: 'Dash',
                zIndex: 5,
                label: {
                    text: '',
                    style: {
                        color: 'red',
                        fontWeight: 'bold'
                    }
                }
            }];
        }

        return {
            ...highChartLocaleOptions,
            exporting: {
                ...exportingOptions.exporting,
                filename: `timeseries_comparison_hs`
            },
            title: {
                text: t('hsComparisonGraph.title', 'Stations Comparison - HS')
            },
            series: series,
            xAxis: xAxis,
            yAxis: [
                {
                    title: {
                        text: 'HS [cm]',
                        gridLineWidth: 1
                    }
                },
            ],
            tooltip: {
                shared: true,
                crosshairs: true,
                xDateFormat: '%Y-%m-%d %H:%M',
                formatter: function () {
                    let tooltip = '<b>' + Highcharts.dateFormat('%Y-%m-%d %H:%M', this.x) + '</b>';
                    this.points.forEach(point => {
                        tooltip += '<br/><span style="color:' + point.series.color + '">●</span> ' + point.series.name + ': <b>' + Math.round(point.y) + '</b>';
                    });
                    return tooltip;
                }
            },
            plotOptions: {
                series: {
                    marker: {
                        enabled: false,
                        symbol: 'circle',
                    },
                    line: {
                        connectNulls: false
                    },
                    states: {
                        inactive: {
                            opacity: 1
                        }
                    }
                }
            },
            chart: {
                zoomType: 'x',
                height: 500,
                ...chartLogoOptions
            },
            ...creditsOptions,
        }
    }, [series, highChartLocaleOptions, t, highlightedDate]);

    return (
        <Stack>
            <HighchartsReact
                highcharts={Highcharts}
                options={options}
            />
        </Stack>
    );
}

export default TimeseriesHSComparisonGraph;
